import { FcmToken } from '@griply/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  queryKey as cachedTokenQueryKey,
  useCachedPushNotificationTokenQuery,
} from 'shared/hooks/queries/use-cached-push-notification-token-query';
import {
  queryKey as fcmTokenDocQueryKey,
  useFcmTokenDocQuery,
} from 'shared/hooks/queries/use-fcm-token-doc-query';
import { useNotificationPermission } from 'shared/hooks/use-notification-permission';
import { fetchFreshPushToken } from 'shared/services/backend-api';
import { getPushNotificationTokenDate } from 'shared/services/local-storage';
import { ID } from 'shared/types/id';

export enum ServiceWorkerStatus {
  Missing = 'missing',
  Registered = 'registered',
  Active = 'active',
}

type PushDiagnosticsProbes = {
  serviceWorker: ServiceWorkerStatus;
  liveToken: string | null;
  liveTokenError: string | null;
  tokenDate: Date | null;
};

export type PushDiagnostics = PushDiagnosticsProbes & {
  permission: PermissionState;
  cachedToken: string | null;
  liveTokenMatchesCached: boolean | null;
  tokenDoc: FcmToken | null;
  isElectron: boolean;
  isRefreshing: boolean;
  refresh: () => Promise<void>;
};

const probeServiceWorker = async (): Promise<ServiceWorkerStatus> => {
  const registration =
    await window.navigator.serviceWorker?.getRegistration?.();
  if (!registration) {
    return ServiceWorkerStatus.Missing;
  }
  return registration.active
    ? ServiceWorkerStatus.Active
    : ServiceWorkerStatus.Registered;
};

const probeLiveToken = () =>
  fetchFreshPushToken()
    .then((token) => ({ liveToken: token ?? null, liveTokenError: null }))
    .catch((error: unknown) => ({
      liveToken: null,
      liveTokenError: error instanceof Error ? error.message : String(error),
    }));

export const createPushDiagnosticsProbesQueryFn =
  (uid: ID) => async (): Promise<PushDiagnosticsProbes> => {
    const [serviceWorker, liveTokenResult] = await Promise.all([
      probeServiceWorker(),
      probeLiveToken(),
    ]);

    return {
      serviceWorker,
      ...liveTokenResult,
      tokenDate: getPushNotificationTokenDate(uid),
    };
  };

export const queryKey = 'push-diagnostics-probes';

// platform-specific twin of mobile's usePushDiagnostics (web-only browser probes)
export const usePushDiagnostics = (uid?: ID): PushDiagnostics => {
  const client = useQueryClient();
  const [permission] = useNotificationPermission();
  const { data: cachedToken } = useCachedPushNotificationTokenQuery(uid);
  const { data: tokenDoc } = useFcmTokenDocQuery(cachedToken);

  const {
    data: probes,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: [queryKey, uid ?? ''],
    queryFn: createPushDiagnosticsProbesQueryFn(uid!),
    enabled: !!uid,
  });

  const refresh = async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: [cachedTokenQueryKey] }),
      client.invalidateQueries({ queryKey: [fcmTokenDocQueryKey] }),
      refetch(),
    ]);
  };

  const liveToken = probes?.liveToken ?? null;

  return {
    permission,
    serviceWorker: probes?.serviceWorker ?? ServiceWorkerStatus.Missing,
    cachedToken: cachedToken ?? null,
    tokenDate: probes?.tokenDate ?? null,
    liveToken,
    liveTokenError: probes?.liveTokenError ?? null,
    liveTokenMatchesCached:
      liveToken && cachedToken ? liveToken === cachedToken : null,
    tokenDoc: tokenDoc ?? null,
    isElectron: !!window.electron,
    isRefreshing: isFetching,
    refresh,
  };
};
